/**
 * Account Provider — OPTIONAL persistent accounts (cookie-BFF) on top of
 * Susurro's guest-first model.
 *
 * Susurro works WITHOUT an account: identity belongs to the device and chat is P2P.
 * This layer adds the ability to RECOVER a profile on a new device with
 * `handle` + passphrase. The session token lives in an **HttpOnly** cookie
 * (`susurro.session`) that this code NEVER reads or stores: the browser
 * attaches it via `credentials:"include"` (cookie-BFF pattern, "token outside JS").
 *
 * The server only sees a `verifier` (hash of the passphrase, derived on the
 * client) and an encrypted profile `blob` — it cannot see the passphrase or
 * decrypt the profile (zero-knowledge, same as the blind relay).
 */

import {
  createContext,
  createResource,
  createSignal,
  type ParentComponent,
  useContext,
} from "solid-js";

import { AccountClient, type AccountProfile, type AccountSession } from "@susurro/protocol";

interface AccountContextValue {
  /**
   * Active session (handle) or `undefined` when the user is a guest **or
   * when it is not known yet**. Does NOT suspend: see {@link AccountProvider}.
   */
  session: () => AccountSession | undefined;
  /** True while the initial session is being resolved (cookie read). */
  loading: () => boolean;
  /**
   * Failure when asking for the session (relay down, CORS, 5xx). P23/I3: "no
   * response" ≠ "you are a guest" — a 401 returns `undefined` WITHOUT error; only
   * a real failure reaches here.
   */
  error: () => unknown;
  /** Asks for the session again. A real retry, not a reload. */
  retry: () => void;
  /** True while an operation (register/login/logout) is in flight. */
  busy: () => boolean;
  /** Creates an account and opens a session. Throws on error (e.g. handle taken). */
  register: (handle: string, passphrase: string, profile?: AccountProfile) => Promise<void>;
  /** Verifies credentials, recovers the profile and opens a session. Throws if invalid. */
  login: (handle: string, passphrase: string) => Promise<AccountProfile>;
  /** Closes the session (returns to guest). */
  logout: () => Promise<void>;
}

const AccountContext = createContext<AccountContextValue>();

/** Relay origin (`apps/signaling`). In dev/e2e it is "" → same-origin (Vite proxy). */
function relayBaseUrl(): string {
  return window.__APP_CONFIG__?.p2pSignalingUrl ?? "";
}

export const AccountProvider: ParentComponent = (props) => {
  const client = new AccountClient(relayBaseUrl());
  const [busy, setBusy] = createSignal(false);

  // Hydrates the initial session by reading the cookie (via the server). Guest-first:
  // a 401 (no cookie) returns `undefined` and the app keeps working P2P.
  //
  // P23/I3 — the `catch` that used to be here returned `undefined` on ANY
  // failure, so with the relay down the account screen claimed "you are
  // a guest" and offered to create an account to someone who already has one. It did
  // not know: there was no response. The error propagates and the UI distinguishes it.
  const [session, { mutate, refetch }] = createResource<AccountSession | undefined>(() =>
    client.me(),
  );

  /**
   * NON-SUSPENDING read of the resource (P23/I1).
   *
   * `createResource` is, along with solid-query's `.data`, the other Solid
   * primitive that suspends: calling the accessor while the promise is in flight
   * triggers the nearest `<Suspense>`. Here the ONLY boundary is in
   * `routes/__root.tsx` with `fallback={null}` and there is **no persistent shell**
   * (each route paints its whole page), so suspending left
   * `document.body.innerText === ""` — the ENTIRE app blank — for the whole
   * flight of `GET /api/accounts/me`. Measured with a probe, not deduced:
   * `e2e/p23-loading-states.spec.ts`.
   *
   * `state` and `latest` do not suspend: `state` is a normal signal and `latest`
   * only falls back to the suspending `read()` if the resource has not resolved yet — a case
   * the guard excludes. It is the exact equivalent of solid-query's `isSuccess`,
   * and for the same reason `!loading` would NOT work: on error it is also `false`.
   */
  const currentSession = (): AccountSession | undefined =>
    session.state === "ready" || session.state === "refreshing" ? session.latest : undefined;

  const run = async <T,>(op: () => Promise<T>): Promise<T> => {
    setBusy(true);
    try {
      return await op();
    } finally {
      setBusy(false);
    }
  };

  const register: AccountContextValue["register"] = (handle, passphrase, profile) =>
    run(async () => {
      const s = await client.register(handle, passphrase, profile ?? {});
      mutate(s);
      void refetch();
    });

  const login: AccountContextValue["login"] = (handle, passphrase) =>
    run(async () => {
      const result = await client.login(handle, passphrase);
      mutate({ handle: result.handle });
      void refetch();
      return result.profile;
    });

  const logout: AccountContextValue["logout"] = () =>
    run(async () => {
      await client.logout();
      mutate(undefined);
    });

  return (
    <AccountContext.Provider
      value={{
        session: currentSession,
        // Only the FIRST resolution: during a `refetch` there is already a value and repainting
        // the skeleton would be a flicker (e.g. after registering).
        loading: () => session.state === "pending" || session.state === "unresolved",
        // `resource.error` is typed `any`: it is narrowed to `unknown` here so
        // no consumer uses it without checking.
        error: () =>
          session.state === "errored" ? ((session.error as unknown) ?? true) : undefined,
        retry: () => void refetch(),
        busy,
        register,
        login,
        logout,
      }}
    >
      {props.children}
    </AccountContext.Provider>
  );
};

export const useAccount = (): AccountContextValue => {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error("useAccount must be used within AccountProvider");
  }
  return ctx;
};
